import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { axiosInstance } from "../App";
import { FileText, Upload, Download, Trash2, File, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

function ProjectDocuments({ projectId, currentUserId }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, [projectId]);

  const fetchDocuments = async () => {
    try {
      const response = await axiosInstance.get(`/projects/${projectId}/documents`);
      setDocuments(response.data);
    } catch (error) {
      toast.error("Dökümanlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error("Dosya boyutu 50MB'dan küçük olmalıdır");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Lütfen bir dosya seçin");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      await axiosInstance.post(`/projects/${projectId}/documents`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Döküman yüklendi");
      setDialogOpen(false);
      setSelectedFile(null);
      fetchDocuments();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || "Dosya yüklenemedi");
      } else if (error.response?.status === 403) {
        toast.error("Döküman yükleme yetkiniz yok");
      } else {
        toast.error("Dosya yüklenemedi");
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (documentId, filename) => {
    try {
      const response = await axiosInstance.get(`/documents/${documentId}/download`, {
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Dosya indiriliyor");
    } catch (error) {
      toast.error("Dosya indirilemedi");
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm("Bu dökümanı silmek istediğinizden emin misiniz?")) return;

    try {
      await axiosInstance.delete(`/documents/${documentId}`);
      toast.success("Döküman silindi");
      fetchDocuments();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Bu dökümanı silme yetkiniz yok");
      } else {
        toast.error("Döküman silinemedi");
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const getFileIcon = (filename) => {
    const extension = filename.split(".").pop().toLowerCase();
    const iconColor = {
      pdf: "text-red-500",
      doc: "text-blue-500",
      docx: "text-blue-500",
      xls: "text-green-500",
      xlsx: "text-green-500",
      ppt: "text-orange-500",
      pptx: "text-orange-500",
      txt: "text-gray-500",
      jpg: "text-purple-500",
      jpeg: "text-purple-500",
      png: "text-purple-500",
      zip: "text-yellow-500",
    };

    return <FileText className={`h-8 w-8 ${iconColor[extension] || "text-gray-400"}`} />;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
          <File className="h-5 w-5" />
          Dökümanlar ({documents.length})
        </h3>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="upload-document-button">
              <Upload className="h-4 w-4" />
              Döküman Yükle
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Döküman Yükle</DialogTitle>
              <DialogDescription>Projeye döküman ekleyin (Maks: 50MB)</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4">
              <div className="space-y-2">
                <input
                  type="file"
                  onChange={handleFileSelect}
                  className="w-full p-2 border rounded-lg"
                  data-testid="file-input"
                />
                {selectedFile && (
                  <div className="text-sm text-gray-600">
                    <p>Dosya: {selectedFile.name}</p>
                    <p>Boyut: {formatFileSize(selectedFile.size)}</p>
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={uploading || !selectedFile} data-testid="submit-upload">
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Yükleniyor...
                  </>
                ) : (
                  "Yükle"
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {documents.length === 0 ? (
        <div className="text-center py-16 text-gray-500" data-testid="no-documents">
          <File className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <p className="text-lg font-medium">Henüz döküman yok</p>
          <p className="text-sm mt-2">Projeye döküman yükleyerek başlayın</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="documents-grid">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="border rounded-lg p-4 hover:shadow-md transition-shadow"
              data-testid={`document-${doc.id}`}
            >
              <div className="flex items-start gap-3 mb-3">
                {getFileIcon(doc.filename)}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate" title={doc.filename}>
                    {doc.filename}
                  </p>
                  <p className="text-xs text-gray-500">{formatFileSize(doc.file_size)}</p>
                </div>
              </div>
              <div className="text-xs text-gray-500 mb-3">
                <p>Yükleyen: {doc.uploaded_by_name}</p>
                <p>{format(new Date(doc.uploaded_at), "d MMM yyyy HH:mm", { locale: tr })}</p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 gap-1"
                  onClick={() => handleDownload(doc.id, doc.filename)}
                  data-testid={`download-${doc.id}`}
                >
                  <Download className="h-3 w-3" />
                  İndir
                </Button>
                {(doc.uploaded_by === currentUserId) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(doc.id)}
                    data-testid={`delete-${doc.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ProjectDocuments;
